import asTransactionCode from '../../../../utilities/asTransactionCode';
import notHandledHere from './notHandledHere';
import withEntryCorrelationIds from './transactionEntryCorrelation';
import isBatchedTransactionFanOutEnabled from './batchedTransactionFanOut';

const _p = { isBatchedTransactionFanOutEnabled };
export const _private = _p;

export default {
    verb: 'willCreate',
    type: 'transaction',
    excludedNamespaceRelations: notHandledHere,
    prerequisites: [],
    priority: -10, // run after the default willCreate rule runs
    description: 'Prepare to create the transaction record on the database',
    //this is the actual logic:
    logic
};

/**
 * @typedef {import("rulesengine.io").LoggingProvider} LoggingProvider
 * @typedef {import("rulesengine.io").WorkflowStack} WorkflowStack
 * @typedef {import("rulesengine.io").Context} Context
 */

/**
 * @param {{
 *   data: T;
 *   prerequisiteResults: object[];
 *   context: Context;
 *   workflowStack: WorkflowStack[];
 *   dispatch: (data:object,context:Context,awaitResult?:boolean)=>Promise<void|any>
 *   log: LoggingProvider
 * }} parameters
 * @returns {T}
 */
async function logic({ data }) {
    // extract changes for the relation being affected by the transaction
    // e.g. extract changes for item relation which is being affected by the item-transaction
    // relation of this transaction.
    const {
        newRecord: { $loki, entries, meta, title, _id, ...changes },
        offlineAction
    } = data;
    const transactionTitle = title || asTransactionCode(data.newRecord._id);
    // Mint the per-entry patch-record ids here, before anything consumes them, so the id the
    // client writes its optimistic `{relation}-patch` record under is the same id the server
    // fans the batch out under.  See transactionEntryCorrelation.
    const correlatedEntries = withEntryCorrelationIds(entries);
    // This is the new <relation>-transaction record
    const newRecord = { $loki, _id, title: transactionTitle, entries: correlatedEntries, meta };
    // These are the updates for the <relation> record
    data.changes = changes;
    // Set the newRecord passed to doingCreate for <relation>-transaction (now that the
    // changes for the <relation> affected by the transaction are removed)
    data.newRecord = newRecord;
    // When the server applies the batch, the wire payload carries `changes` as well, so the whole
    // batch goes in one request instead of one PATCH per entry (GH #1521).  It is a transport
    // field only: the server strips it before persisting, so the stored <relation>-transaction
    // record keeps the shape it has always had.  Do not persist it locally either.
    // Gated because a server that predates the fan-out would ignore `changes` -- see
    // batchedTransactionFanOut.
    const batchedFanOut = _p.isBatchedTransactionFanOutEnabled();
    offlineAction.create.newRecord = batchedFanOut ? { ...newRecord, changes } : newRecord;
    // doingCreate reads this to decide whether to queue its own per-entry PATCHes.  Decided here,
    // once, so both halves of the request cannot disagree about which mode this transaction is in.
    data.batchedFanOut = batchedFanOut;
    return data;
}
